import type { useReactFlow, Viewport } from '@xyflow/react';

type ReactFlow = ReturnType<typeof useReactFlow>;

// Keep the opened node at least this far from the panel's left edge — wide enough for the node's
// hover actions menu (the three-dots kebab sits ~44px past the node's right edge) to clear the panel.
export const NODE_GAP_PX = 56;

type OverlapInput = {
  /** The opened node's right edge, in screen px (flowToScreenPosition of node.x + width). */
  nodeRightScreen: number;
  /** The React Flow pane's current right edge, in screen px (getBoundingClientRect().right). */
  paneRightScreen: number;
  /** Width of the panel that's about to be on screen. */
  panelWidth: number;
  /** Width of the panel already on screen (0 when none is open). */
  prevPanelWidth: number;
};

// Px to pan left so the opened node clears the panel (0 if already clear). The pane is a flex sibling
// of the panel, so it's *already* shrunk by an open panel's width — only the delta matters, so a
// same-width switch pans nothing while a first open pans the full width. Pure, so it's unit-tested.
export const overlapBehindPanel = ({
  nodeRightScreen,
  paneRightScreen,
  panelWidth,
  prevPanelWidth,
}: OverlapInput): number => {
  const futureRightEdge = paneRightScreen - (panelWidth - prevPanelWidth) - NODE_GAP_PX;
  return Math.max(0, nodeRightScreen - futureRightEdge);
};

// How far to pan the canvas left (px) so a node clears the panel: read live geometry, delegate the math
// to the pure overlapBehindPanel above. 0 when already clear.
const overlapForNode = (reactFlow: ReactFlow, nodeId: string, panelWidth: number, prevPanelWidth: number): number => {
  const node = reactFlow.getNode(nodeId);
  const flowEl = document.querySelector('.react-flow');
  if (!node || !flowEl) return 0;
  const nodeRightFlow = node.position.x + (node.measured?.width ?? 0);
  const nodeRightScreen = reactFlow.flowToScreenPosition({ x: nodeRightFlow, y: node.position.y }).x;
  return overlapBehindPanel({
    nodeRightScreen,
    paneRightScreen: flowEl.getBoundingClientRect().right,
    panelWidth,
    prevPanelWidth,
  });
};

// The strip left of the panel, in screen px, plus the pane's own origin. Built from the stable container
// rather than the `.react-flow` pane's right edge, which is mid-flex-shrink while the panel opens.
const stripBounds = (panelWidth: number) => {
  const flowEl = document.querySelector('.react-flow');
  const containerEl = flowEl?.closest('.playbook-builder');
  if (!flowEl || !containerEl) return null;
  const pane = flowEl.getBoundingClientRect();
  return {
    paneLeft: pane.left,
    paneTop: pane.top,
    centerX: (pane.left + (containerEl.getBoundingClientRect().right - panelWidth)) / 2,
    centerY: (pane.top + pane.bottom) / 2,
  };
};

// The builder's maxZoom: a node found from a zoomed-out view lands readable.
const FOCUS_ZOOM = 1;

// The viewport that puts a focused node in the middle of the strip left of the panel, at FOCUS_ZOOM.
// Centring on the whole pane instead would leave the node behind the panel, since the pane still spans
// the panel's width when the focus is requested.
export const focusViewportForNode = (
  reactFlow: ReactFlow,
  { nodeId, panelWidth }: { nodeId: string; panelWidth: number },
): Viewport | null => {
  const node = reactFlow.getNode(nodeId);
  const strip = stripBounds(panelWidth);
  if (!node || !strip) return null;
  const centerX = node.position.x + (node.measured?.width ?? 0) / 2;
  const centerY = node.position.y + (node.measured?.height ?? 0) / 2;
  // setViewport's x and y are offsets inside the pane, so the pane's own screen origin comes off.
  return {
    x: strip.centerX - strip.paneLeft - centerX * FOCUS_ZOOM,
    y: strip.centerY - strip.paneTop - centerY * FOCUS_ZOOM,
    zoom: FOCUS_ZOOM,
  };
};

// Screen-x of the graph's main vertical spine, or null when nothing is measured. The dagre TB layout
// stacks the primary path in one column and fans branches out sideways, so most nodes share the spine's
// x and the branches are the minority tails — the median node-center picks the spine, where the mean
// (bounding-box midpoint) would be dragged off-center by branch fan-out.
const graphSpineScreenX = (reactFlow: ReactFlow): number | null => {
  const centers = reactFlow
    .getNodes()
    .filter(node => (node.measured?.width ?? 0) > 0)
    .map(node => reactFlow.flowToScreenPosition({ x: node.position.x + (node.measured?.width ?? 0) / 2, y: 0 }).x)
    .sort((a, b) => a - b);
  if (centers.length === 0) return null;
  return centers[Math.floor(centers.length / 2)];
};

// Signed px to pan the canvas left as a panel opens (negative pans right), or 0 for no move.
// - anchorless (Test) drawer: no node of its own, so center the graph's spine in the strip left of the
//   panel. The strip is the *container* (stable) minus the panel width, not the `.react-flow` pane
//   (mid-flex-shrink while opening) — so one clean tween lands on the final position.
// - a node drawer: slide the opened node clear of the panel (0 if it's already visible).
export const panDxForPanel = (
  reactFlow: ReactFlow,
  {
    anchorless,
    nodeId,
    panelWidth,
    prevPanelWidth,
  }: {
    anchorless: boolean;
    nodeId: string;
    panelWidth: number;
    prevPanelWidth: number;
  },
): number => {
  if (anchorless) {
    const strip = stripBounds(panelWidth);
    const spineCenter = graphSpineScreenX(reactFlow);
    if (!strip || spineCenter === null) return 0;
    return spineCenter - strip.centerX;
  }
  if (!reactFlow.getNode(nodeId)) return 0;
  return overlapForNode(reactFlow, nodeId, panelWidth, prevPanelWidth);
};

// The viewport after the pan that clears an opening panel, or null when nothing moves. On a phone the
// panel overlays the whole canvas rather than shrinking it, so a pan would only strand the graph
// off-screen once the panel closes.
export const panelViewport = (
  reactFlow: ReactFlow,
  { isMobile, ...input }: Parameters<typeof panDxForPanel>[1] & { isMobile: boolean },
): Viewport | null => {
  const dx = panDxForPanel(reactFlow, input);
  if (dx === 0 || isMobile) return null;
  const { x, y, zoom } = reactFlow.getViewport();
  return { x: x - dx, y, zoom };
};
